/**
 * Print-ready credential certificate. Open this route + browser "Save as PDF"
 * produces a perfect PDF — no server-side PDF library required.
 *
 * Routes:
 *   /certificate/credential/:credentialNumber  (BCHHP / BCT / BCDTS)
 *   /certificate/license/:licenseNumber        (LHT)
 *   /certificate/school/:accreditationNumber   (Institutional/Programmatic)
 */
import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seal from "@/components/Seal";
import { credentialLabel } from "@/lib/labels";
import { Printer, Download, AlertCircle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

const VERIFY_BASE =
  (typeof window !== "undefined" ? window.location.origin : "") + "/verify";

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return String(d).slice(0, 10); }
}

export default function Certificate() {
  const { kind, identifier } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (authLoading || !user) return;
    (async () => {
      try {
        let resolved = null;
        if (kind === "credential") {
          const r = await api.get(`/certificates/credential/${identifier}`);
          resolved = {
            type: "credential",
            holder_name: r.data.holder_name,
            credential_type: r.data.credential_type,
            number: r.data.credential_number,
            issue_date: r.data.issue_date,
            expiry_date: r.data.expiry_date,
            status: r.data.status,
            organization: r.data.organization,
            verify_url: `${VERIFY_BASE}/${r.data.credential_number}`,
            label: credentialLabel(r.data.credential_type),
            authority_line: "International Board of Trichology & Holistic Medicine",
            title_line: "Certificate of Board Certification",
          };
        } else if (kind === "license") {
          const r = await api.get(`/certificates/license/${identifier}`);
          const lic = r.data;
          resolved = {
            type: "license",
            holder_name: lic.holder_name,
            credential_type: "LHT",
            number: lic.license_number,
            issue_date: lic.issued_on,
            expiry_date: lic.expires_on,
            status: lic.status,
            organization: "IBTHM",
            verify_url: `${VERIFY_BASE}/${lic.holder_credential_number || lic.license_number}`,
            label: "Licensed Holistic Trichologist (LHT)",
            authority_line: "International Board of Trichology & Holistic Medicine",
            title_line: "Certificate of Professional Licensure",
          };
        } else if (kind === "school") {
          const r = await api.get(`/certificates/school/${identifier}`);
          const sch = r.data;
          const accType = sch.accreditation_type || "institutional";
          resolved = {
            type: "school",
            holder_name: sch.name,
            credential_type: accType,
            number: sch.accreditation_number,
            issue_date: sch.accredited_at,
            expiry_date: sch.expires_at,
            status: sch.accreditation_status,
            organization: "ACIS",
            verify_url: `${VERIFY_BASE}/${sch.accreditation_number}`,
            label: accType === "programmatic" ? "Programmatic Accreditation" : "Institutional Accreditation",
            authority_line: "Accreditation Commission for Integrative Sciences",
            title_line: "Certificate of Accreditation",
          };
        }
        setData(resolved);
      } catch (e) {
        if (e?.response?.status === 403) {
          setForbidden(true);
        } else if (e?.response?.status === 401) {
          // ProtectedRoute will redirect; in case it doesn't, set message
          setErr("Authentication required");
        } else {
          setErr(e?.response?.data?.detail || e.message || "Could not load certificate");
        }
      }
    })();
  }, [kind, identifier, user, authLoading]);

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Verifying access…</div>;
  }
  if (!user) {
    return <Navigate to={`/login?next=/certificate/${kind}/${identifier}`} replace />;
  }
  if (forbidden) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="text-center max-w-md" data-testid="cert-forbidden">
          <Lock className="h-10 w-10 text-slate-500 mx-auto mb-2" />
          <h1 className="text-xl font-heading text-primary mb-2">Restricted certificate</h1>
          <p className="text-sm text-slate-600">
            Certificates and licenses are accessible only to the credential holder, the accredited institution,
            and authorized administrators. To verify a credential, please use the{" "}
            <a href={`/verify/${identifier}`} className="text-primary underline hover:no-underline">public verification page</a>.
          </p>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="text-center" data-testid="cert-error">
          <AlertCircle className="h-10 w-10 text-amber-600 mx-auto mb-2" />
          <h1 className="text-xl font-heading text-primary mb-1">Certificate not available</h1>
          <p className="text-sm text-slate-600">{err}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading certificate…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 py-10 print:py-0 print:bg-white" data-testid="certificate-page">
      <div className="max-w-[850px] mx-auto mb-4 px-4 flex justify-end gap-2 print:hidden">
        <Button variant="outline" onClick={() => window.print()} data-testid="cert-print-btn">
          <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
        </Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="cert-download-btn">
          <Download className="h-4 w-4 mr-2" /> Download
        </Button>
      </div>

      <div
        id="certificate-paper"
        className="max-w-[850px] mx-auto bg-white shadow-2xl print:shadow-none border border-slate-200 print:border-0 relative aspect-[11/8.5] print:aspect-auto overflow-hidden"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at top, rgba(120, 86, 168, 0.06), transparent 60%), radial-gradient(ellipse at bottom, rgba(10, 37, 64, 0.05), transparent 60%)",
        }}
      >
        {/* Border ornament */}
        <div className="absolute inset-4 border-2 border-primary/70 print:border-primary pointer-events-none" />
        <div className="absolute inset-6 border border-primary/30 pointer-events-none" />

        <div className="relative h-full px-14 py-10 flex flex-col">
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] font-bold text-slate-500 mb-1">
              {data.authority_line}
            </div>
            <div className="font-heading text-3xl text-primary font-bold mb-1">
              {data.title_line}
            </div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">
              {data.label}
            </div>
          </div>

          <div className="mt-8 text-center">
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">This is to certify that</div>
            <div className="font-heading text-4xl text-primary font-bold border-b border-primary/50 inline-block pb-1 px-4" data-testid="cert-holder">
              {data.holder_name}
            </div>
            <div className="mt-4 text-sm text-slate-700 max-w-xl mx-auto leading-relaxed">
              has met all requirements established by the {data.authority_line} and is hereby granted
              the rights, privileges, and responsibilities associated with this designation,
              subject to the continued maintenance of compliance and renewal.
            </div>
          </div>

          <div className="mt-auto pt-6 flex items-end justify-between gap-6">
            <div className="text-[11px] text-slate-700 space-y-1">
              <Detail label="Credential number" value={data.number} mono />
              <Detail label="Issue date"        value={fmt(data.issue_date)} />
              <Detail label="Expiration date"   value={fmt(data.expiry_date)} />
              <Detail label="Status"            value={(data.status || "").replace(/_/g, " ").toUpperCase()} />
            </div>
            <div className="flex flex-col items-center gap-1">
              <Seal credentialType={data.credential_type} size="xl" />
              <span className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-bold">Official Authority Seal</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="rounded-sm border border-slate-200 bg-white p-1">
                <QRCodeCanvas
                  value={data.verify_url}
                  size={120}
                  bgColor="#ffffff"
                  fgColor="#0A2540"
                  level="M"
                  includeMargin={false}
                  data-testid="cert-qr"
                />
              </div>
              <span className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-bold">Scan to verify</span>
              <span className="text-[9px] text-slate-500 break-all max-w-[160px] text-center">{data.verify_url}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[850px] mx-auto mt-4 px-4 text-center text-[10px] text-slate-500 print:hidden">
        This certificate is a public, verifiable artifact. Print or save as PDF using your browser's print dialog (recommended margins: minimum / no headers).
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          #certificate-paper { box-shadow: none !important; border: 0 !important; }
          @page { size: letter landscape; margin: 0.4in; }
        }
      `}</style>
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="uppercase tracking-[0.18em] text-[9px] text-slate-500 font-bold w-28">{label}</span>
      <span className={`text-slate-800 ${mono ? "font-mono-data" : ""}`}>{value}</span>
    </div>
  );
}
